
import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry monitoring if a DSN is provided.
 */
const SENTRY_DSN = "https://50185f0b046292c17e64c29333a7d126@o4507509900443648.ingest.us.sentry.io/4507509901885440";

// Singleton flag to prevent multiple initializations
let isInitialized = false;

export function initMonitoring() {
  // Prevent multiple initializations
  if (isInitialized) {
    if (import.meta.env.MODE === "development") {
      console.log("Sentry monitoring already initialized, skipping...");
    }
    return;
  }

  if (!SENTRY_DSN || SENTRY_DSN.includes("your-dsn-key-here")) {
    if (import.meta.env.MODE === "development") {
      console.warn("Sentry DSN not set or is placeholder. Monitoring is disabled.");
    }
    return;
  }

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false, 
          blockAllMedia: false,
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0, // Capture 10% of transactions in prod, all in dev
      // Session Replay
      replaysOnErrorSampleRate: 1.0, // If an error happens during a session, replay is 100% likely to be captured
      replaysSessionSampleRate: import.meta.env.MODE === 'production' ? 0.01 : 0.1, // Capture 1% of sessions in prod, 10% in dev
      
      environment: import.meta.env.MODE,
      sendDefaultPii: true, // Send Personally Identifiable Information
      
      // Enhanced error context
      beforeSend(event) {
        // Don't report errors during local development unless explicitly enabled
        if (import.meta.env.MODE === 'development' && !import.meta.env.VITE_ENABLE_SENTRY) {
          return null;
        }
        return event;
      },
      
      // Attach user info when available
      beforeBreadcrumb(breadcrumb) {
        return breadcrumb;
      }
    });

    // Mark as initialized only after successful initialization
    isInitialized = true;

    if (import.meta.env.MODE === "development") {
      console.log("Sentry monitoring initialized successfully.");
    }
  } catch (error) {
    console.error("Failed to initialize Sentry monitoring:", error);
  }
}

// Report an error with enhanced context
export function reportError(error: unknown, errorInfo?: unknown) {
  if (!isInitialized) {
    console.warn("Sentry not initialized, cannot report error:", error);
    return;
  }
  
  Sentry.captureException(error, { extra: { errorInfo } });
  if (import.meta.env.MODE === 'development') {
    console.error("Sentry reported error:", error, errorInfo);
  }
}

// Set user information for better error context
export function setUserContext(user: { id: string; email?: string; role?: string }) {
  if (!user?.id || !isInitialized) return;
  
  Sentry.setUser({
    id: user.id,
    email: user.email,
    ...(user.role && { role: user.role })
  });
}

// Clear user context on logout
export function clearUserContext() {
  if (!isInitialized) return;
  Sentry.setUser(null);
}
